import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Award, CheckCircle, Clock, AlertCircle, Calendar,
  Download, User, CreditCard, TrendingUp, Activity, Crown, Sparkles
} from 'lucide-react';
import { SubscriptionCTACard } from './SubscriptionCTA';
import RecurringPaymentBanner from './RecurringPaymentBanner';

const EnhancedDashboard = ({ member, membershipPlan, onNavigate }) => {
  const { t } = useTranslation();
  const getMembershipStatus = () => {
    if (!membershipPlan) return { status: t('membership.noActivePlan'), color: 'gray', icon: AlertCircle };
    
    // Check member's actual membership status, not the plan's status
    const memberStatus = member?.membership_status;
    
    if (memberStatus === 'active') {
      return { status: t('membership.active'), color: 'green', icon: CheckCircle };
    } else if (memberStatus === 'pending' || memberStatus === 'pending_payment') {
      return { status: t('membership.pendingPayment'), color: 'yellow', icon: Clock };
    } else {
      return { status: t('membership.inactive'), color: 'gray', icon: AlertCircle };
    }
  };

  // Helper to safely format dates
  const formatMemberDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
        return null;
      }
      return date.toLocaleDateString();
    } catch {
      return null;
    }
  };

  // Get member since date - prefer membership_start_date, fall back to created_at
  const memberSinceDate = formatMemberDate(member?.membership_start_date) || 
                          formatMemberDate(member?.created_at) || 
                          formatMemberDate(membershipPlan?.created_at);

  const statusInfo = getMembershipStatus();
  const StatusIcon = statusInfo.icon;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Subscription CTA Card - Shows for users without membership, expired, or expiring soon */}
      <SubscriptionCTACard 
        member={member} 
        membershipPlan={membershipPlan} 
        onNavigate={onNavigate}
      />

      {/* Recurring Payment Banner - Prominent position */}
      <RecurringPaymentBanner 
        member={member}
        onNavigateToPaymentMethods={() => onNavigate('membership')}
        onUpdate={() => {}}
      />

      {/* Membership Status Card - Mobile Optimized */}
      <Card className="border-2 border-teal-100 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-teal-50 to-blue-50 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
            <div className="min-w-0">
              <CardTitle className="text-lg sm:text-2xl flex items-center gap-2">
                <Award className="h-5 w-5 sm:h-6 sm:w-6 text-teal-600 flex-shrink-0" />
                <span className="truncate">{membershipPlan?.name || t('membership.noActivePlan')}</span>
              </CardTitle>
              <CardDescription className="text-sm sm:text-base mt-1 sm:mt-2">
                {memberSinceDate ? t('membership.memberSince', { date: memberSinceDate }) : t('membership.startMembershipToday')}
              </CardDescription>
            </div>
            <Badge className={`bg-${statusInfo.color}-100 text-${statusInfo.color}-800 self-start sm:self-auto flex-shrink-0`}>
              <StatusIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              <span className="text-xs sm:text-sm">{statusInfo.status}</span>
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:pt-6">
          {membershipPlan ? (
            <div className="grid grid-cols-3 gap-2 sm:gap-6">
              <div className="text-center p-2 sm:p-4 bg-gray-50 rounded-lg">
                <div className="text-lg sm:text-3xl font-bold text-teal-600">
                  ${membershipPlan.price || membershipPlan.payment_amount}
                </div>
                <div className="text-xs sm:text-sm text-gray-600 mt-1">
                  <span className="hidden sm:inline">{t('membership.monthlyPayment')}</span>
                  <span className="sm:hidden">{t('membership.payment')}</span>
                </div>
              </div>
              <div className="text-center p-2 sm:p-4 bg-gray-50 rounded-lg">
                <div className="text-lg sm:text-3xl font-bold text-blue-600">
                  {membershipPlan.duration_months || 1}
                </div>
                <div className="text-xs sm:text-sm text-gray-600 mt-1">
                  <span className="hidden sm:inline">{t('membership.monthsDuration')}</span>
                  <span className="sm:hidden">{t('membership.duration')}</span>
                </div>
              </div>
              <div className="text-center p-2 sm:p-4 bg-gray-50 rounded-lg">
                <div className="text-lg sm:text-3xl font-bold text-purple-600">
                  {membershipPlan.benefits?.length || 0}
                </div>
                <div className="text-xs sm:text-sm text-gray-600 mt-1">
                  <span className="hidden sm:inline">{t('membership.benefitsIncluded')}</span>
                  <span className="sm:hidden">{t('membership.benefits')}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-6 sm:py-8">
              <AlertCircle className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
              <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">{t('membership.noActiveMembershipYet')}</p>
              <Button 
                onClick={() => onNavigate('membership')}
                className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-sm sm:text-base"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                {t('membership.browseMembershipPlans')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions Grid - Mobile Optimized (2x2 grid on mobile) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <Button
          onClick={() => onNavigate('appointments')}
          className="h-20 sm:h-24 bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-200 flex flex-col items-center justify-center p-2"
        >
          <Calendar className="h-6 w-6 sm:h-8 sm:w-8 mb-1 sm:mb-2 text-teal-600" />
          <span className="font-semibold text-xs sm:text-sm text-center">{t('quickActions.bookAppointment')}</span>
        </Button>

        <Button
          onClick={() => onNavigate('membership')}
          className={`h-20 sm:h-24 flex flex-col items-center justify-center p-2 border-2 ${
            !membershipPlan 
              ? 'bg-gradient-to-br from-teal-50 to-emerald-50 hover:from-teal-100 hover:to-emerald-100 border-teal-300 text-gray-900' 
              : 'bg-white hover:bg-gray-50 border-gray-200 text-gray-900'
          }`}
        >
          <Crown className={`h-6 w-6 sm:h-8 sm:w-8 mb-1 sm:mb-2 ${!membershipPlan ? 'text-teal-600' : 'text-amber-500'}`} />
          <span className="font-semibold text-xs sm:text-sm text-center">
            {!membershipPlan ? t('subscription.getMembership') : t('tabs.subscription')}
          </span>
        </Button>

        <Button
          onClick={() => onNavigate('card')}
          className="h-20 sm:h-24 bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-200 flex flex-col items-center justify-center p-2"
          disabled={!membershipPlan}
        >
          <Download className="h-6 w-6 sm:h-8 sm:w-8 mb-1 sm:mb-2 text-blue-600" />
          <span className="font-semibold text-xs sm:text-sm text-center">{t('quickActions.downloadCard')}</span>
        </Button>

        <Button
          onClick={() => onNavigate('profile')}
          className="h-20 sm:h-24 bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-200 flex flex-col items-center justify-center p-2"
        >
          <User className="h-6 w-6 sm:h-8 sm:w-8 mb-1 sm:mb-2 text-purple-600" />
          <span className="font-semibold text-xs sm:text-sm text-center">{t('quickActions.manageProfile')}</span>
        </Button>
      </div>

      {/* Benefits List - Mobile Optimized */}
      {membershipPlan && membershipPlan.benefits && membershipPlan.benefits.length > 0 && (
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-teal-600" />
              {t('membership.yourMembershipBenefits')}
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              {t('membership.enjoyBenefitsWith', { plan: membershipPlan.name })}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {membershipPlan.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-gray-50 rounded-lg">
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Usage Statistics - Mobile Optimized */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <Card>
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">{t('stats.appointmentsThisMonth')}</p>
                <p className="text-2xl sm:text-3xl font-bold text-teal-600">0</p>
              </div>
              <Calendar className="h-8 w-8 sm:h-12 sm:w-12 text-teal-200" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">{t('stats.benefitsUsed')}</p>
                <p className="text-2xl sm:text-3xl font-bold text-blue-600">0%</p>
              </div>
              <Activity className="h-8 w-8 sm:h-12 sm:w-12 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">{t('stats.membershipValue')}</p>
                <p className="text-2xl sm:text-3xl font-bold text-green-600">${membershipPlan?.price || 0}</p>
              </div>
              <TrendingUp className="h-8 w-8 sm:h-12 sm:w-12 text-green-200" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EnhancedDashboard;